/**
 * Flight folio scan shared schemas.
 *
 * Browser-safe: imports only zod. Consumed by the API
 * (flightFolioScan.service.ts + the Claude extraction in lib/claude.client.ts,
 * where this schema doubles as the structured-output format) and the app (via
 * the @@api alias) for rendering the extracted paper-folio data.
 *
 * Conventions:
 * - Every cell is a plain string holding the value exactly as written on
 *   the paper sheet ("13:45", "2419:58", "9196", …) — no normalization, no
 *   numeric parsing. An empty string means blank or unreadable. Plain strings
 *   (not nullable) because the structured-outputs API caps union-typed
 *   parameters at 16 and this schema has ~100 cells.
 * - Kept structured-outputs compatible: no regex/min/max constraints, no
 *   recursion, objects only extend/compose.
 * - Imported from 'zod/v4' (not 'zod'): the Anthropic SDK's zodOutputFormat
 *   helper is typed against zod v4 schemas, which zod 3.25+ exposes via this
 *   subpath. The rest of the codebase stays on the v3 import.
 */
import { z } from 'zod/v4'

const cell = (description: string) =>
  z
    .string()
    .describe(
      `${description} — transcribed exactly as written; empty string if blank or illegible`
    )

export const folioHeaderSchema = z.object({
  aircraftType: cell('Aircraft type'),
  registrationNo: cell('Registration number'),
  flightNo: cell('Flight number'),
  natureOfFlight: cell('Nature of flight (e.g. Part 91)'),
  logNo: cell('Log number (printed, usually red)'),
})

// Column cells shared by the Totals Brought Forward row, each leg row, and
// the Totals row of the main grid.
export const folioHoursCellsSchema = z.object({
  flightTime: cell('Flight time'),
  airframeHours: cell('Airframe hours'),
  engineHoursNo1: cell('Engine hours no. 1'),
  engineHoursNo2: cell('Engine hours no. 2'),
  engineHoursNo3: cell('Engine hours no. 3'),
  engineCyclesNo1: cell('Engine cycles no. 1'),
  engineCyclesNo2: cell('Engine cycles no. 2'),
  engineCyclesNo3: cell('Engine cycles no. 3'),
  totalLandings: cell('Total landings'),
  apuHours: cell('APU hours'),
})

export const folioLegSchema = folioHoursCellsSchema.extend({
  date: cell('Leg date'),
  from: cell('Route details FR (departure airport)'),
  to: cell('Route details TO (arrival airport)'),
  pax: cell('PAX count'),
  crewCpt: cell('Crew CPT'),
  crewFo: cell('Crew F/O'),
  crewOther: cell('Crew OTHER'),
  offBlockUtc: cell('Off block UTC'),
  onBlockUtc: cell('On block UTC'),
  takeOffUtc: cell('T/O UTC'),
  landingUtc: cell('LND UTC'),
  distNm: cell('Distance NM'),
  distKm: cell('Distance KM'),
})

export const folioFuelSchema = z.object({
  plannedUpliftLts: cell('Fuel planned uplift LTS'),
  actualUpliftLts: cell('Fuel actual uplift LTS'),
  fuelDocNo: cell('Fuel doc no.'),
  departTotalLbs: cell('Depart total LBS'),
  arrivalTotalLbs: cell('Arrival total LBS'),
  lbsUsed: cell('LBS used'),
})

export const folioDeiceSchema = z.object({
  start: cell('De-ice/anti-ice start'),
  stop: cell('De-ice/anti-ice stop'),
  type: cell('De-ice/anti-ice type'),
  ratioMix: cell('De-ice/anti-ice ratio (mix)'),
})

export const folioOilSchema = z.object({
  engineOilUpliftNo1: cell('Engine oil uplift no. 1'),
  engineOilUpliftNo2: cell('Engine oil uplift no. 2'),
  engineOilUpliftNo3: cell('Engine oil uplift no. 3'),
  apuOilUplift: cell('APU oil uplift'),
  hydOilUplift: cell('HYD oil uplift'),
  psiDif: cell('PSI DIF'),
})

export const folioEctmReadingSchema = z.object({
  value1: cell('Column 1 value'),
  value2: cell('Column 2 value'),
  value3: cell('Column 3 value'),
})

export const folioEctmSchema = z.object({
  weight: folioEctmReadingSchema,
  oatSat: folioEctmReadingSchema,
  ias: folioEctmReadingSchema,
  tas: folioEctmReadingSchema,
  mach: folioEctmReadingSchema,
  fl: folioEctmReadingSchema,
  fuelFlow: folioEctmReadingSchema,
  n1Prop: folioEctmReadingSchema,
  n2Ng: folioEctmReadingSchema,
  itt: folioEctmReadingSchema,
  oilPress: folioEctmReadingSchema,
  oilTemp: folioEctmReadingSchema,
  amps: folioEctmReadingSchema,
  fuelTemp: folioEctmReadingSchema,
  bleedPress: folioEctmReadingSchema,
})

export const folioInspectionsSchema = z.object({
  nextScheduled: cell('Next scheduled inspection'),
  dueHourly: cell('Due hourly'),
  dueCalendar: cell('Due calendar'),
})

export const folioSignOffSchema = z.object({
  preFlightCaptainName: cell('Pre flight captain name'),
  preFlightLicenceNumber: cell('Pre flight licence number'),
  preFlightDate: cell('Pre flight date'),
  postFlightCaptainName: cell('Post flight captain name'),
  postFlightLicenceNumber: cell('Post flight licence number'),
  postFlightDate: cell('Post flight date'),
})

export const folioDefectSchema = z.object({
  defectYn: cell('DEFECT Y/N column'),
  description: cell('Defect description (DEFECT — A = ACTION / D = DEFER)'),
  actionCode: cell('A/D/P column'),
  signature: cell('Signature column (name if readable, otherwise "signed")'),
  date: cell('Date column'),
  actionTaken: cell('Action taken by engineer/pilot'),
  melRef: cell('MEL ref'),
  stamp: cell('Stamp column'),
})

export const folioExtractionSchema = z.object({
  header: folioHeaderSchema,
  totalsBroughtForward: folioHoursCellsSchema.describe(
    'The "Totals Brought Forward" row of the main grid'
  ),
  legs: z
    .array(folioLegSchema)
    .describe('Leg rows 1-4 of the main grid, top to bottom; omit fully blank rows'),
  totals: folioHoursCellsSchema.describe('The "Totals" row of the main grid'),
  fuel: folioFuelSchema,
  deice: folioDeiceSchema,
  oil: folioOilSchema,
  ectm: folioEctmSchema.describe(
    'Engine Condition Trend Monitoring grid; all-empty cells if entirely blank'
  ),
  inspections: folioInspectionsSchema,
  signOff: folioSignOffSchema,
  defects: z
    .array(folioDefectSchema)
    .describe('Defect record rows with any writing; empty array if none'),
  warnings: z
    .array(z.string())
    .describe('Notes about cells that were ambiguous, partially legible, or guessed'),
  lowConfidenceFields: z
    .array(z.string())
    .describe(
      "Dot-paths of cells whose reading is less than 90% certain (e.g. 'fuel.fuelDocNo', 'legs.0.offBlockUtc', 'oil.psiDif'); leg and defect rows by zero-based index. The app refuses to auto-fill these."
    ),
})

export type FolioExtraction = z.infer<typeof folioExtractionSchema>
export type FolioLeg = z.infer<typeof folioLegSchema>
export type FolioHoursCells = z.infer<typeof folioHoursCellsSchema>

export const flightFolioScanStatusValues = ['processing', 'completed', 'failed'] as const
export type FlightFolioScanStatus = (typeof flightFolioScanStatusValues)[number]

// Media types the extraction accepts. HEIC is deliberately excluded — the app
// limits its file input's `accept` to these so iOS transcodes HEIC to JPEG.
export const folioScanMediaTypes = ['image/jpeg', 'image/png', 'image/webp'] as const
export type FolioScanMediaType = (typeof folioScanMediaTypes)[number]

// Display labels for the drawer's result tables
export const folioSectionLabels = {
  header: 'Flight details',
  legs: 'Route, times & totals',
  fuel: 'Fuel',
  deice: 'De-ice / Anti-ice',
  oil: 'Oil uplift',
  ectm: 'ECTM',
  inspections: 'Inspections',
  signOff: 'Captain sign-off',
  defects: 'Defects',
} as const
